import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { api, JobApplication } from '../services/api';

interface JobApplicationDetailProps {
  apiUrl: string;
  audience: string;
}

const JobApplicationDetail: React.FC<JobApplicationDetailProps> = ({ apiUrl, audience }) => {
  const { id } = useParams<{ id: string }>();
  const [application, setApplication] = useState<JobApplication | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { getAccessTokenSilently } = useAuth0();

  useEffect(() => {
    const fetchApplicationDetail = async () => {
      if (!id) {
        setError('Application ID is missing');
        setLoading(false);
        return;
      }

      try {
        // Make sure we have a token before making the API call
        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience: audience, // Value from config.json
            scope: 'read', // Scope that exists for the API being called. You can create these through the Auth0 Management API or through the Auth0 Dashboard in the Permissions view of your API.
          }
        });
        //await getAccessTokenSilently();
        const data = await api.getJobApplication(apiUrl, id, token);
        setApplication(data);
        setLoading(false);
      } catch (err) {
        console.error(`Error fetching application with ID ${id} from ${apiUrl}:`, err);
        setError('Failed to load job application details. Please try again later.');
        setLoading(false);
      }
    };

    fetchApplicationDetail();
  }, [apiUrl, audience, id, getAccessTokenSilently]);

  if (loading) {
    return <div className="loading">Loading job application details...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  if (!application) {
    return <div className="error">Job application not found.</div>;
  }

  return (
    <div className="job-detail">
      <Link to="/applications" className="back-link">← Back to Applications</Link>

      <div className="card">
        <h2>{application.position}</h2>
        <p>
          <span className={`status-badge status-${application.status}`}>
            {application.status}
          </span>
        </p>

        <div style={{ marginTop: '20px' }}>
          <p><strong>Company:</strong> {application.company}</p>
          <p><strong>Date Applied:</strong> {new Date(application.dateApplied).toLocaleDateString()}</p>

          {application.contactName && (
            <p><strong>Contact:</strong> {application.contactName}</p>
          )}

          {application.contactEmail && (
            <p><strong>Contact Email:</strong> {application.contactEmail}</p>
          )}

          {application.notes && (
            <div>
              <h3>Notes</h3>
              <p>{application.notes}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default JobApplicationDetail;